import { gql } from 'graphql-request';
import {
  HeadToHeadFilters,
  HeadToHeadMatch,
  HeadToHeadMatchesFilters,
  HeadToHeadStats,
  Nullable,
  User,
  UserRole,
  UserWithToken,
  WithData,
  WithMeta,
} from '@ygoargentina/sdk/api/api.types';
import { getGraphQLQueryClient } from './query-client';
import { z } from 'zod';
import { AxiosRequestConfig } from 'axios';
import axiosClient from './request-client';

export const MockedUser = {
  id: -1,
  name: 'Yugi',
  lastName: 'Muto',
  email: 'yugi.muto@kaiba-corp.com',
  cossyId: '0403254323',
  password: new Array(8).fill('*').join(''),
};

export const cossyIdLoginSchema = z.object({
  cossyId: z.string().length(10, { message: 'Konami ID inválido' }),
  password: z.string().min(1, { message: 'Ingresa tu contraseña' }),
  service: z.string(),
  version: z.string(),
});

export const emailLoginSchema = z.object({
  email: z.string().email({ message: 'Email inválido' }),
  password: z.string().min(1, { message: 'Ingresa tu contraseña' }),
  service: z.string(),
  version: z.string(),
});

export const emailRecoverySchema = z.object({
  email: z
    .string()
    .min(1, { message: 'Ingresa tu email' })
    .email({ message: 'Email inválido' }),
});

export const cossyIdRecoverySchema = z.object({
  cossyId: z.string().length(10, { message: 'Konami ID inválido' }),
});

export const editUserSchema = z.object({
  name: z.string(),
  secondName: z.string().optional(),
  lastName: z.string(),
  secondLastName: z.string().optional(),
  email: z.string().email({ message: 'Email inválido' }).optional(),
  countryId: z.number({ message: 'País inválido' }).optional(),
  stateId: z.number({ message: 'Estado inválido' }).optional(),
  city: z.string({ message: 'Ingresa tu ciudad' }).optional(),
  homeStoreId: z.number().nullable().optional(),
  favoriteCommunityId: z.number().nullable().optional(),
  isStore: z.boolean().optional(),
  isKonamiGuest: z.boolean().optional(),
  anonymityMode: z.enum(['NONE', 'INITIAL', 'ANONYMOUS']).optional(),
});

export const editUserIdsSchema = z.object({
  cossyId: z.string().optional(),
  duelLinksId: z.string().optional(),
  masterDuelId: z.string().optional(),
  neuronId: z.string().optional(),
  discordId: z.string().optional(),
  duelingBookId: z.string().optional(),
  challongeId: z.string().optional(),
  topDeckId: z.string().optional(),
  topDeckNick: z.string().optional(),
  limitlessId: z.string().optional(),
  limitlessNick: z.string().optional(),
});

export const mergeUsersSchema = z.object({
  sourceUserId: z.number().min(1, 'Usuario origen requerido'),
  targetUserId: z.number().min(1, 'Usuario destino requerido'),
  deleteSource: z.boolean().default(true).optional(),
});

export const passwordValidation = z
  .string()
  .min(1, { message: 'Ingresa tu contraseña' })
  .min(8, { message: 'La contraseña debe tener al menos 8 caracteres' })
  .max(128, { message: 'La contraseña debe tener menos de 128 caracteres' })
  .regex(/[A-Z]/, {
    message: 'La contraseña debe contener al menos una mayúscula',
  })
  .regex(/[a-z]/, {
    message: 'La contraseña debe contener al menos una minúscula',
  })
  .regex(/\d/, { message: 'La contraseña debe contener al menos un número' })
  .regex(/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/, {
    message:
      'La contraseña debe contener al menos un carácter especial (!@#$%^&*()_+-=[]{};\':"|,.<>/?)',
  });

export const resetPasswordSchema = z
  .object({
    password: passwordValidation,
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Las contraseñas no coinciden',
    path: ['confirmPassword'],
  });

export const loginSchema = z
  .object({
    cossyId: z
      .string()
      .length(10, { message: 'Konami ID inválido' })
      .optional(),
    email: z.string().email({ message: 'Email inválido' }).optional(),
    password: z.string().min(1, { message: 'Ingresa tu contraseña' }),
    service: z.string(),
    version: z.string(),
  })
  .refine((data) => data.cossyId || data.email, {
    message: 'Ingresa tu Konami ID o email',
    path: ['cossyId'],
  });

export const firstTimeSchema = z.object({
  cossyId: z.string().length(10, { message: 'Konami ID inválido' }),
  password: z.string().min(1, { message: 'Ingresa tu contraseña' }),
  service: z.string(),
  version: z.string(),
});

export const changePasswordSchema = z
  .object({
    password: passwordValidation,
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Las contraseñas no coinciden.',
    path: ['confirmPassword'],
  });

export const registerSchema = z
  .object({
    name: z
      .string()
      .min(1, { message: 'Ingresa tu nombre' })
      .min(2, { message: 'El nombre debe tener al menos 2 caracteres' }),
    lastName: z
      .string()
      .min(1, { message: 'Ingresa tu apellido' })
      .min(2, { message: 'El apellido debe tener al menos 2 caracteres' }),
    email: z
      .string()
      .min(1, { message: 'Ingresa tu email' })
      .email({ message: 'Email inválido' }),
    cossyId: z.string().optional(),
    password: passwordValidation,
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Las contraseñas no coinciden.',
    path: ['confirmPassword'],
  });

export const createUserSchema = z
  .object({
    name: z
      .string()
      .min(1, { message: 'Ingresa el nombre' })
      .min(2, { message: 'El nombre debe tener al menos 2 caracteres' }),
    secondName: z.string().optional(),
    lastName: z
      .string()
      .min(1, { message: 'Ingresa el apellido' })
      .min(2, { message: 'El apellido debe tener al menos 2 caracteres' }),
    secondLastName: z.string().optional(),
    email: z
      .string()
      .min(1, { message: 'Ingresa el email' })
      .email({ message: 'Email inválido' }),
    password: passwordValidation,
    confirmPassword: z.string(),
    roleId: z.string().optional(),
    emailValidated: z.boolean().optional(),
    cossyValidated: z.boolean().optional(),
    storeId: z.number().optional(),
    /** Lo da de alta como empleado del organizador (ver `CreateUserInput`). */
    organizerId: z.number().optional(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Las contraseñas no coinciden.',
    path: ['confirmPassword'],
  });

export const addEmployeeSchema = z.object({
  userId: z.number().min(1, 'Selecciona un usuario'),
  role: z.nativeEnum(UserRole),
});

export const quickCreateUserSchema = z
  .object({
    name: z
      .string()
      .min(1, { message: 'Ingresa el nombre' })
      .min(2, { message: 'El nombre debe tener al menos 2 caracteres' }),
    lastName: z
      .string()
      .min(1, { message: 'Ingresa el apellido' })
      .min(2, { message: 'El apellido debe tener al menos 2 caracteres' }),
    email: z
      .string()
      .min(1, { message: 'Ingresa el email' })
      .email({ message: 'Email inválido' }),
    password: passwordValidation,
    confirmPassword: z.string(),
    role: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Las contraseñas no coinciden.',
    path: ['confirmPassword'],
  });

export const setPasswordSchema = z
  .object({
    password: passwordValidation,
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Las contraseñas no coinciden.',
    path: ['confirmPassword'],
  });

export const listUsers = async (
  user: Nullable<UserWithToken>,
  variables?: Record<string, unknown>
) =>
  getGraphQLQueryClient(user).request<WithMeta<User>>(
    gql`
      query users($filters: UserFilters) {
        data: users(filters: $filters) {
          data {
            id
            name
            secondName
            lastName
            secondLastName
            email

            stateId
            province {
              id
              name
            }

            createdAt
            updatedAt
            deletedAt
            lastLoginAt

            cossyValidated

            cossyId
            masterDuelId
            duelLinksId
            duelingBookId
            neuronId
            challongeId
            topDeckId
            topDeckNick
            limitlessId
            limitlessNick
            discordId

            homeStoreId
            homeStore {
              id
              name
              logoUrl

              organizers {
                id
              }
            }

            favoriteCommunityId
            favoriteCommunity {
              id
              name
              logoUrl
            }

            lastTournamentPlayedAt
            isJudge
          }
          meta {
            total
            perPage
            lastPage
          }
        }
      }
    `,
    { filters: variables }
  );

export const getMe = async (user: Nullable<UserWithToken>) =>
  getGraphQLQueryClient(user).request<WithData<User>>(gql`
    query me {
      data: me {
        id
        name
        secondName
        lastName
        secondLastName
        nickname
        nicknameVisibility
        email
        countryId
        stateId
        city
        homeStoreId
        favoriteCommunityId
        anonymityMode

        picture

        cossyId
        masterDuelId
        duelLinksId
        discordId
        duelingBookId
        challongeId
        neuronId

        hasPlayedTournament

        apiKeys {
          id
          key
          createdAt
          expiresAt
        }

        # El staff de la sesión: lo mismo que trae /auth/me en las apps web.
        # La super-app lo vuelve a pedir cuando el API avisa session:changed.
        employees {
          id
          storeId
          role
        }
        organizerEmployees {
          id
          organizerId
          role
        }
        tournamentsOrganized {
          id
        }
      }
    }
  `);

export const getUser = async (user: Nullable<UserWithToken>, userId: number) =>
  getGraphQLQueryClient(user).request<WithData<User>>(
    gql`
      query user($id: Int!) {
        data: user(id: $id) {
          id
          name
          secondName
          lastName
          secondLastName
          sleeveId
          nickname
          nicknameVisibility
          email
          countryId
          stateId
          city
          homeStoreId
          favoriteCommunityId
          isStore
          isKonamiGuest
          anonymityMode
          picture

          cossyId
          cossyValidated
          masterDuelId
          duelLinksId
          discordId
          duelingBookId
          challongeId
          neuronId
          topDeckId
          topDeckNick
          limitlessId
          limitlessNick

          apiKeys {
            id
            key
            createdAt
            expiresAt
          }

          tournaments {
            id
            title
            dateFrom
            dateTo
            status
            currentRound
            format {
              id
              name
            }
            organizer {
              id
              name
              logoUrl
              store {
                id
                name
              }
            }
            results {
              position
            }
          }
        }
      }
    `,
    { id: userId }
  );

/**
 * Alta/edición del propio usuario de Dueling Book. El id sale de la sesión en
 * el API, no del cliente: no sirve para editar la cuenta de otro.
 */
export const updateMyDuelingBookId = async (
  user: Nullable<UserWithToken>,
  duelingBookId: string
) =>
  getGraphQLQueryClient(user).request<WithData<User>>(
    gql`
      mutation updateMyDuelingBookId($duelingBookId: String!) {
        data: updateMyDuelingBookId(duelingBookId: $duelingBookId) {
          id
          duelingBookId
        }
      }
    `,
    { duelingBookId }
  );

export const updateUser = async (
  user: Nullable<UserWithToken>,
  id: number,
  input: Partial<User>
) =>
  getGraphQLQueryClient(user).request<WithData<User>>(
    gql`
      mutation updateUser($id: Int!, $input: UpdateUserInput!) {
        data: updateUser(id: $id, input: $input) {
          id
          name
          secondName
          lastName
          secondLastName
          nickname
          nicknameVisibility
          email
          roleId
          cossyId
          # Vuelve en la respuesta para que el switch del folder muestre lo que
          # quedó guardado y no lo que se pidió: el resolver sólo lo acepta de
          # un admin, así que "lo mandé" y "se guardó" no son lo mismo.
          cossyValidated
          picture

          masterDuelId
          duelLinksId
          neuronId
          discordId
          duelingBookId
          challongeId
          topDeckId
          topDeckNick
          limitlessId
          limitlessNick

          isStore
          isKonamiGuest
          emailValidated

          employees {
            storeId
            role
          }
        }
      }
    `,
    { id, input }
  );

export const createApiKey = async (
  user: Nullable<UserWithToken>,
  userId: number
) =>
  getGraphQLQueryClient(user).request<WithData<User>>(
    gql`
      mutation createUserApiKey($userId: Int!) {
        data: createUserApiKey(userId: $userId) {
          id
        }
      }
    `,
    { userId }
  );

// Public lookup by cossyId. The API never returns the real email here (cossyIds
// are enumerable); `emailMasked` is a recognition hint and is null for
// temporary/ghost accounts — use `isTmpEmail` to detect those.
export interface UserLookup {
  id: number;
  name: string | null;
  lastName: string | null;
  cossyId: string | null;
  firstLoginAt: string | null;
  deletedAt: string | null;
  emailMasked: string | null;
  isTmpEmail: boolean;
  /**
   * La cuenta la creó el sistema (pre-inscripción, import, organizador) y su
   * contraseña sigue siendo el placeholder: hay que pedirle una nueva en vez
   * de un login. Usá esto y NO `firstLoginAt`: esa columna se agregó en
   * sep-2025 sin backfill y el login normal no la escribe, así que está en
   * null en toda cuenta vieja que sí tiene contraseña propia.
   */
  needsPassword: boolean;
}

export const searchUser = async (cossyId: string) =>
  getGraphQLQueryClient(null).request<WithData<UserLookup>>(
    gql`
      mutation searchUser($cossyId: String!) {
        data: searchUser(cossyId: $cossyId) {
          id
          name
          lastName
          cossyId
          firstLoginAt
          deletedAt
          emailMasked
          isTmpEmail
          needsPassword
        }
      }
    `,
    { cossyId }
  );

export interface PublicUser {
  id: number;
  name: string;
  lastName: string;
  cossyId: string | null;
  neuronId: string | null;
  masterDuelId: string | null;
  duelLinksId: string | null;
  duelingBookId: string | null;
  challongeId: string | null;
  topDeckId: string | null;
  topDeckNick: string | null;
  discordId: string | null;
  anonymityMode: string | null;
}

export const publicUsers = async (filters?: {
  search?: string;
  page?: number;
  limit?: number;
}) =>
  getGraphQLQueryClient(null).request<WithMeta<PublicUser>>(
    gql`
      query publicUsers($filters: PublicUserFilters) {
        data: publicUsers(filters: $filters) {
          data {
            id
            name
            lastName
            cossyId
            neuronId
            masterDuelId
            duelLinksId
            duelingBookId
            challongeId
            topDeckId
            topDeckNick
            discordId
            anonymityMode
          }
          meta {
            total
            perPage
            lastPage
          }
        }
      }
    `,
    { filters }
  );

export const registerUser = async (
  input: Omit<z.infer<typeof registerSchema>, 'confirmPassword'>
): Promise<WithData<UserWithToken>> => {
  // Import authService dynamically to avoid circular dependencies
  const { authService } = await import('./auth-service');

  const result = await authService.register({
    name: input.name,
    lastName: input.lastName,
    email: input.email || undefined,
    cossyId: input.cossyId || undefined,
    password: input.password,
  });

  if (result.error || !result.data?.user) {
    throw new Error(
      result.detail || result.message || 'registration_failed'
    );
  }

  return {
    data: {
      ...result.data.user,
      accessToken: result.data.user.accessToken || result.data.accessToken,
    } as UserWithToken,
  };
};

export const uploadAvatar = (
  body: FormData,
  config?: AxiosRequestConfig
): Promise<User> =>
  axiosClient.post(`/imports/user-avatar`, body, {
    headers: {
      'Content-Type': 'multipart/form-data',
      ...config?.headers,
    },
  });

export const changePassword = async (
  user: Nullable<UserWithToken>,
  input: z.infer<typeof changePasswordSchema>
) =>
  getGraphQLQueryClient(user).request<WithData<User>>(
    gql`
      mutation changePassword($password: String!) {
        data: changePassword(password: $password) {
          id
        }
      }
    `,
    { password: input.password }
  );

export const adminResetPassword = async (
  user: Nullable<UserWithToken>,
  userId: number,
  password?: string
) =>
  getGraphQLQueryClient(user).request<WithData<User>>(
    gql`
      mutation adminResetPassword($userId: Int!, $password: String) {
        data: adminResetPassword(userId: $userId, password: $password) {
          id
        }
      }
    `,
    { userId, password }
  );

export const validateEmail = async (hash: string) =>
  getGraphQLQueryClient(null).request<WithData<UserWithToken>>(
    gql`
      mutation validateEmail($hash: String!) {
        data: validateEmail(hash: $hash) {
          id
          name
          lastName
          email
          roleId
          cossyId
          picture

          emailValidated

          employees {
            storeId
            role
          }
        }
      }
    `,
    { hash }
  );

export const getMyHeadToHeadStats = async (
  user: Nullable<UserWithToken>,
  filters?: HeadToHeadFilters
) =>
  getGraphQLQueryClient(user).request<WithData<HeadToHeadStats[]>>(
    gql`
      query myHeadToHeadStats($filters: HeadToHeadFilters) {
        data: myHeadToHeadStats(filters: $filters) {
          opponent {
            id
            name
            lastName
            cossyId
            picture
          }
          wins
          losses
          draws
          total
        }
      }
    `,
    { filters }
  );

export const getMyHeadToHeadMatches = async (
  user: Nullable<UserWithToken>,
  filters: HeadToHeadMatchesFilters
) =>
  getGraphQLQueryClient(user).request<WithData<HeadToHeadMatch[]>>(
    gql`
      query myHeadToHeadMatches($filters: HeadToHeadMatchesFilters!) {
        data: myHeadToHeadMatches(filters: $filters) {
          id
          tournamentId
          tournamentName
          round
          table
          result
          player1Score
          player2Score
          date
        }
      }
    `,
    { filters }
  );

export const resendValidationEmail = async (user: Nullable<UserWithToken>) =>
  getGraphQLQueryClient(user).request<WithData<User>>(gql`
    mutation resendValidationEmail {
      data: resendValidationEmail {
        id
      }
    }
  `);

export const createUser = async (
  user: Nullable<UserWithToken>,
  input: Omit<z.infer<typeof createUserSchema>, 'confirmPassword'>
) =>
  getGraphQLQueryClient(user).request<WithData<User>>(
    gql`
      mutation createUser($input: CreateUserInput!) {
        data: createUser(input: $input) {
          id
          name
          lastName
          email
          roleId
          emailValidated
          cossyValidated
        }
      }
    `,
    { input }
  );

export const hasToDownloadUsers = async (user: Nullable<UserWithToken>) =>
  getGraphQLQueryClient(user).request<WithData<User>>(gql`
    query hasToDownloadUsers {
      data: hasToDownloadUsers
    }
  `);

export const updateUserLastUsersDownloadAt = async (
  user: Nullable<UserWithToken>
) =>
  getGraphQLQueryClient(user).request<WithData<User>>(gql`
    mutation updateUserLastUsersDownloadAt {
      data: updateUserLastUsersDownloadAt {
        id
      }
    }
  `);

export const mergeUsers = async (
  user: Nullable<UserWithToken>,
  sourceUserId: number,
  targetUserId: number,
  deleteSource?: boolean
) =>
  getGraphQLQueryClient(user).request<{
    data: {
      targetUser: User;
      mergedRecordsCount: Record<string, number>;
      deletedSourceUser: boolean;
    };
  }>(
    gql`
      mutation mergeUsers(
        $sourceUserId: Int!
        $targetUserId: Int!
        $deleteSource: Boolean
      ) {
        data: mergeUsers(
          sourceUserId: $sourceUserId
          targetUserId: $targetUserId
          deleteSource: $deleteSource
        ) {
          targetUser {
            id
            name
            lastName
            email
          }
          mergedRecordsCount
          deletedSourceUser
        }
      }
    `,
    { sourceUserId, targetUserId, deleteSource }
  );

export const getTemporaryKonamiId = async (
  user: Nullable<UserWithToken>,
  organizerId: number
) =>
  getGraphQLQueryClient(user).request<WithData<string>>(
    gql`
      query getTemporaryKonamiId($organizerId: Int!) {
        data: getTemporaryKonamiId(organizerId: $organizerId)
      }
    `,
    { organizerId }
  );

export const assignOrganizerTemporaryCossyId = async (
  user: Nullable<UserWithToken>,
  userId: number,
  organizerId: number
) =>
  getGraphQLQueryClient(user).request<WithData<User>>(
    gql`
      mutation assignOrganizerTemporaryCossyId(
        $userId: Int!
        $organizerId: Int!
      ) {
        data: assignOrganizerTemporaryCossyId(
          userId: $userId
          organizerId: $organizerId
        ) {
          id
        }
      }
    `,
    { userId, organizerId }
  );
